import React, { useState } from "react";
import {
  IconBoxMultipleFilled,
  IconCalendarFilled,
  IconCheck,
  IconCircleCheckFilled,
  IconClipboardListFilled,
  IconClockFilled,
  IconDiscountFilled,
  IconEyeFilled,
  IconEyeOff,
  IconLayoutGridFilled,
  IconReceiptFilled,
  IconSettingsFilled,
  IconShoppingCartFilled,
  IconTagFilled,
} from "@tabler/icons-react";
import { Button, FieldError, Input, Select } from "../utils/ui";

const permissionIcons = {
  punto_de_venta: { icon: IconShoppingCartFilled, color: "bg-blue-500" },
  gestion_pedidos: { icon: IconClipboardListFilled, color: "bg-indigo-500" },
  gestion_articulos: { icon: IconTagFilled, color: "bg-cyan-500" },
  promociones_categoria: { icon: IconDiscountFilled, color: "bg-amber-400" },
  inventario: { icon: IconBoxMultipleFilled, color: "bg-teal-500" },
  registro_ventas: { icon: IconClockFilled, color: "bg-purple-500" },
  gastos: { icon: IconReceiptFilled, color: "bg-pink-500" },
  gestion_diaria: { icon: IconCalendarFilled, color: "bg-orange-500" },
  configuraciones: { icon: IconSettingsFilled, color: "bg-slate-400" },
};

const CAJERO_PRESET = ["punto_de_venta", "gestion_pedidos", "registro_ventas"];

export default function CuentaForm({
  initial = null,
  permisos = [],
  onSubmit,
  onCancel,
  saving = false,
  serverError = null,
}) {
  const isEdit = Boolean(initial);
  const initialIds = new Set((initial?.permission_ids || []).map(Number));
  const [fullName, setFullName] = useState(initial?.full_name ?? "");
  const [username, setUsername] = useState(initial?.username ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [status, setStatus] = useState(initial?.status ?? "active");
  // Al editar se precarga la contraseña actual para poder verla y corregirla.
  // Viene de users.password_plain; el hash bcrypt no es reversible.
  const [password, setPassword] = useState(initial?.password_plain ?? "");
  const [showPassword, setShowPassword] = useState(false);
  const [selected, setSelected] = useState(initialIds);
  const [error, setError] = useState("");

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyPreset = (slugs) => {
    const bySlug = new Map(permisos.map((p) => [p.slug, Number(p.id)]));
    setSelected(new Set(slugs.map((slug) => bySlug.get(slug)).filter(Boolean)));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!fullName.trim()) return setError("El nombre completo es obligatorio.");
    if (!username.trim()) return setError("El usuario de acceso es obligatorio.");
    if (!isEdit && !password) return setError("La contraseña es obligatoria al crear la cuenta.");
    // Al editar solo se manda la contraseña si el dueño la cambió: si se envía
    // igual, el backend regenera el hash bcrypt en cada guardado sin motivo.
    const original = initial?.password_plain ?? "";
    const cambioPassword = isEdit ? password !== original : Boolean(password);
    if (isEdit && password && password.length < 6)
      return setError("La contraseña debe tener al menos 6 caracteres.");
    onSubmit({
      full_name: fullName.trim(),
      username: username.trim(),
      email: email || null,
      phone: phone || null,
      status,
      permission_ids: Array.from(selected).sort((a, b) => a - b),
      ...(cambioPassword ? { password } : {}),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
        <div className="grid grid-cols-1 content-start gap-4 md:grid-cols-2">
          <Input
            label="Nombre completo *"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Ej. María López"
          />
          <Input
            label="Usuario de acceso *"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Ej. maria.lopez"
          />
          <Input
            label="Correo"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Ej. maria@empresa.mx"
          />
          <Input
            label="Teléfono"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Ej. 664-123-4567"
          />
          <Select label="Estado" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Activa</option>
            <option value="inactive">Inactiva</option>
          </Select>
          <div className="relative">
            <span className="mb-1.5 block text-sm font-semibold text-white/70">
              Contraseña {isEdit ? "" : "*"}
            </span>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isEdit ? "Sin contraseña registrada" : "Mínimo 6 caracteres"}
                minLength={6}
                autoComplete="off"
                className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 pr-10 text-white outline-none transition-colors placeholder:text-white/25 focus:border-brand focus:ring-1 focus:ring-brand"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 transition-colors hover:text-white/80"
              >
                {showPassword ? (
                  <IconEyeOff className="h-4 w-4" stroke={1.75} />
                ) : (
                  <IconEyeFilled className="h-4 w-4" stroke={1.75} />
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-white/70">
              Vistas que verá esta cuenta · {selected.size}/{permisos.length}
            </p>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setSelected(new Set(permisos.map((p) => Number(p.id))))}
                className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] text-white transition-colors hover:bg-white/25"
              >
                Todas
              </button>
              <button
                type="button"
                onClick={() => applyPreset(CAJERO_PRESET)}
                className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] text-white transition-colors hover:bg-white/25"
              >
                Cajero
              </button>
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] text-white transition-colors hover:bg-white/25"
              >
                Ninguna
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {permisos.map((p) => {
              const meta = permissionIcons[p.slug] || { icon: IconLayoutGridFilled, color: "bg-slate-400" };
              const Icon = meta.icon;
              const active = selected.has(Number(p.id));
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggle(Number(p.id))}
                  aria-pressed={active}
                  className={`relative flex items-start gap-2 rounded-2xl border p-2.5 text-left transition-colors ${
                    active
                      ? "border-brand bg-brand/20"
                      : "border-white/15 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      active ? meta.color : "bg-white/10"
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${active ? "text-black" : "text-white/50"}`} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-white">{p.name}</span>
                    <span className="mt-0.5 line-clamp-2 block text-xs leading-tight text-gray-400">
                      {p.description}
                    </span>
                  </span>
                  {active && (
                    <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white text-black">
                      <IconCheck className="h-3 w-3" stroke={2.5} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <FieldError message={serverError || error} />

      <div className="flex justify-end gap-3 border-t border-white/10 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" disabled={saving}>
          <IconCircleCheckFilled className="h-4 w-4 text-white" />
          {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear cuenta"}
        </Button>
      </div>
    </form>
  );
}